'use client'

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import type { TocEntry } from '@/lib/help-nav'

// Client island: the right-hand "On this page" TOC. The entries are
// computed on the server from the article's section structure; the ids
// are rendered on the SSR <h2> headings, so this island only adds
// scroll-spy + smooth-scroll behaviour. The body text stays SSR.
export function OnThisPage({ entries }: { entries: TocEntry[] }) {
  const [activeId, setActiveId] = useState<string | null>(entries[0]?.id ?? null)

  useEffect(() => {
    if (entries.length === 0) return
    const headings = entries
      .map((e) => document.getElementById(e.id))
      .filter((el): el is HTMLElement => el !== null)
    if (headings.length === 0) return

    const observer = new IntersectionObserver(
      (observed) => {
        // Pick the topmost heading currently intersecting the upper
        // band of the viewport.
        const visible = observed
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) {
          setActiveId(visible[0].target.id)
        }
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: [0, 1] },
    )
    headings.forEach((h) => observer.observe(h))
    return () => observer.disconnect()
  }, [entries])

  const onClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    const target = document.getElementById(id)
    if (!target) return
    e.preventDefault()
    setActiveId(id)
    target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    history.replaceState(null, '', `#${id}`)
    target.setAttribute('tabindex', '-1')
    ;(target as HTMLElement).focus({ preventScroll: true })
  }

  if (entries.length === 0) return null

  return (
    <nav aria-label="On this page" className="text-sm">
      <div className="mb-3 font-display text-xs font-medium uppercase tracking-widest text-muted-foreground">
        On this page
      </div>
      <ul className="space-y-1 border-l border-border/70">
        {entries.map((e) => {
          const active = e.id === activeId
          return (
            <li key={e.id}>
              <a
                href={`#${e.id}`}
                onClick={(ev) => onClick(ev, e.id)}
                aria-current={active ? 'true' : undefined}
                className={cn(
                  '-ml-px block border-l-2 py-1 pl-3 leading-snug transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
                  active
                    ? 'border-primary font-medium text-primary'
                    : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
                )}
              >
                {e.text}
              </a>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
